// Fotografia de profil, aceeași peste tot (antetul personalului, profilul elevului, contul elevului):
// meniul „Vezi poza” / „Schimbă poza” (sau „Adaugă poză”) / „Șterge poza”, fereastra mică cu poza de
// aproape (staffPhotoView) și confirmarea ștergerii.
//  · photo.mode "upload": se încarcă pe loc (target "me" sau id-ul contului); onChanged(avatarUrl | null)
//  · photo.mode "local": doar previzualizarea (contul încă nu există); onPick(blob, previewUrl), onClear()
// Întoarce ce trebuie pus în pagină (elements: câmpul de fișier, fereastra, confirmarea) și meniul.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { removeAvatar, uploadAvatar } from "../../../api/avatarService";
import { ACCEPTED_TYPES, shrinkPhoto } from "../../../utils/avatarResize";
import { ButtonUI } from "../../UI";
import ConfirmDialog from "../../Common/ConfirmDialog";

export default function useProfilePhoto(photo, { name = "", renderPhoto } = {}) {
   const inputRef = useRef(null);
   const [busy, setBusy] = useState(false);
   const [status, setStatus] = useState({ kind: "", text: "" });
   const [viewOpen, setViewOpen] = useState(false);
   const [confirmOpen, setConfirmOpen] = useState(false);
   const enabled = Boolean(photo);
   const localPreview = photo?.mode === "local" ? photo.previewUrl || null : null;
   const hasPhoto = photo?.mode === "local" ? Boolean(localPreview) : Boolean(photo?.hasPhoto);

   const pick = () => {
      if (!enabled || busy) return;
      setStatus({ kind: "", text: "" });
      inputRef.current?.click();
   };

   const onFile = async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file || !photo) return;
      setBusy(true);
      setStatus({ kind: "", text: "Se pregătește fotografia…" });
      try {
         const blob = await shrinkPhoto(file);
         if (photo.mode === "local") {
            photo.onPick?.(blob, URL.createObjectURL(blob));
            setStatus({ kind: "", text: "" });
         } else {
            setStatus({ kind: "", text: "Se încarcă…" });
            const res = await uploadAvatar(photo.target, blob);
            photo.onChanged?.(res?.avatarUrl || null);
            setStatus({ kind: "ok", text: "Fotografia s-a salvat." });
         }
      } catch (e) {
         setStatus({ kind: "error", text: e?.message || "Nu am putut salva fotografia." });
      } finally {
         setBusy(false);
      }
   };

   const remove = async () => {
      setConfirmOpen(false);
      setViewOpen(false);
      if (!photo) return;
      if (photo.mode === "local") {
         photo.onClear?.();
         return;
      }
      setBusy(true);
      try {
         await removeAvatar(photo.target);
         photo.onChanged?.(null);
         setStatus({ kind: "ok", text: "Fotografia s-a șters." });
      } catch (e) {
         setStatus({ kind: "error", text: e?.message || "Nu am putut șterge fotografia." });
      } finally {
         setBusy(false);
      }
   };

   const menuOptions = [
      { value: "view", label: "Vezi poza", disabled: !hasPhoto },
      { value: "change", label: hasPhoto ? "Schimbă poza" : "Adaugă poză" },
      { value: "remove", label: "Șterge poza", disabled: !hasPhoto },
   ];
   const onMenu = (value) => {
      if (value === "view") setViewOpen(true);
      else if (value === "change") pick();
      else if (value === "remove") setConfirmOpen(true);
   };

   useEffect(() => {
      if (!viewOpen) return undefined;
      const onKey = (event) => {
         if (event.key === "Escape") setViewOpen(false);
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
   }, [viewOpen]);

   const viewer =
      viewOpen && typeof document !== "undefined"
         ? createPortal(
              <div className="staffPhotoView" role="dialog" aria-modal="true" aria-label="Fotografia" onClick={() => setViewOpen(false)}>
                 <div className="staffPhotoView__card" onClick={(event) => event.stopPropagation()}>
                    {renderPhoto?.("staffPhotoView__photo")}
                    {name ? <p className="staffPhotoView__name">{name}</p> : null}
                    <ButtonUI.Row>
                       <ButtonUI
                          onClick={() => {
                             setViewOpen(false);
                             pick();
                          }}
                          disabled={busy}
                       >
                          Schimbă
                       </ButtonUI>
                       <ButtonUI variant="danger" onClick={() => setConfirmOpen(true)} disabled={busy}>
                          Șterge
                       </ButtonUI>
                       <ButtonUI variant="primary" onClick={() => setViewOpen(false)} autoFocus>
                          Închide
                       </ButtonUI>
                    </ButtonUI.Row>
                 </div>
              </div>,
              document.body,
           )
         : null;

   const elements = enabled ? (
      <>
         <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(",")}
            className="staffProfileHead__file"
            onChange={onFile}
            tabIndex={-1}
            aria-hidden="true"
         />
         {viewer}
         <ConfirmDialog
            open={confirmOpen}
            title="Ștergi fotografia?"
            note="În locul ei se vor vedea inițialele."
            confirmLabel="Șterge"
            cancelLabel="Renunță"
            onConfirm={remove}
            onCancel={() => setConfirmOpen(false)}
         />
      </>
   ) : null;

   return {
      enabled,
      busy,
      status,
      hasPhoto,
      localPreview,
      pick,
      openView: () => setViewOpen(true),
      menuOptions,
      onMenu,
      elements,
   };
}
